import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Globe2 } from "lucide-react";
import { getNationRivalry } from "@/lib/social.functions";

/**
 * GLOBAL HEAT MAP™ — synthetic continent grid showing per-country hype
 * intensity. No external map dep. Realtime updates via Supabase channel
 * on hype_pulses.
 */

type Region = "AMERICAS" | "EUROPE" | "AFRICA" | "ASIA" | "OCEANIA";

const COUNTRY_REGION: Record<string, Region> = {
  ARG: "AMERICAS", BRA: "AMERICAS", MEX: "AMERICAS", USA: "AMERICAS", CAN: "AMERICAS",
  URU: "AMERICAS", COL: "AMERICAS", CHI: "AMERICAS", PER: "AMERICAS", ECU: "AMERICAS",
  PAR: "AMERICAS", VEN: "AMERICAS", CRC: "AMERICAS",
  ESP: "EUROPE", FRA: "EUROPE", GER: "EUROPE", ITA: "EUROPE", POR: "EUROPE", ENG: "EUROPE",
  NED: "EUROPE", BEL: "EUROPE", CRO: "EUROPE", SUI: "EUROPE", POL: "EUROPE", DEN: "EUROPE", SRB: "EUROPE",
  MAR: "AFRICA", SEN: "AFRICA", TUN: "AFRICA", EGY: "AFRICA", NGA: "AFRICA", GHA: "AFRICA", CIV: "AFRICA", ALG: "AFRICA",
  JPN: "ASIA", KOR: "ASIA", KSA: "ASIA", IRN: "ASIA", QAT: "ASIA", UAE: "ASIA", UZB: "ASIA",
  AUS: "OCEANIA", NZL: "OCEANIA",
};

const FLAG: Record<string, string> = {
  ARG: "🇦🇷", BRA: "🇧🇷", MEX: "🇲🇽", USA: "🇺🇸", CAN: "🇨🇦",
  ESP: "🇪🇸", FRA: "🇫🇷", GER: "🇩🇪", ITA: "🇮🇹", POR: "🇵🇹",
  ENG: "🏴󠁧󠁢󠁥󠁮󠁧󠁿", URU: "🇺🇾", COL: "🇨🇴", CHI: "🇨🇱", PER: "🇵🇪",
  ECU: "🇪🇨", JPN: "🇯🇵", KOR: "🇰🇷", AUS: "🇦🇺", MAR: "🇲🇦",
  NED: "🇳🇱", BEL: "🇧🇪", CRO: "🇭🇷", SUI: "🇨🇭", SEN: "🇸🇳",
  POL: "🇵🇱", DEN: "🇩🇰", SRB: "🇷🇸", TUN: "🇹🇳", EGY: "🇪🇬", NGA: "🇳🇬",
  GHA: "🇬🇭", CIV: "🇨🇮", ALG: "🇩🇿", KSA: "🇸🇦", IRN: "🇮🇷",
  QAT: "🇶🇦", UAE: "🇦🇪", UZB: "🇺🇿", NZL: "🇳🇿", PAR: "🇵🇾", VEN: "🇻🇪", CRC: "🇨🇷",
};

const REGIONS: { id: Region; label: string }[] = [
  { id: "AMERICAS", label: "Américas" },
  { id: "EUROPE", label: "Europa" },
  { id: "AFRICA", label: "África" },
  { id: "ASIA", label: "Asia" },
  { id: "OCEANIA", label: "Oceanía" },
];

function heatColor(intensity: number): string {
  // 0..1 → oklch green → amber → rose
  const hue = 145 - intensity * 145; // 145 → 0
  const chroma = 0.12 + intensity * 0.18;
  const light = 0.5 + (1 - intensity) * 0.15;
  return `oklch(${light.toFixed(2)} ${chroma.toFixed(2)} ${Math.round(hue)})`;
}

export function GlobalHeatMap() {
  const q = useQuery({
    queryKey: ["heatmap", "nations"],
    queryFn: () => getNationRivalry(),
    refetchInterval: 20_000,
  });
  // PR-SCALE-04 — removed global `heatmap_pulses` postgres_changes subscription.
  // At Mundial scale every hype_pulses INSERT (1000s/min) was fanned out to
  // every Home viewer. Polling every 20s via useQuery is sufficient — the
  // server-side aggregate (recompute_world_pulse) already runs on a 10s cron.

  const rows = q.data?.rows ?? [];
  const max = rows[0]?.hype_total ?? 0;

  const byRegion: Record<Region, { code: string; hype: number }[]> = {
    AMERICAS: [], EUROPE: [], AFRICA: [], ASIA: [], OCEANIA: [],
  };
  for (const r of rows) {
    const code = r.nation_code ?? "";
    const region = COUNTRY_REGION[code];
    if (!region) continue;
    byRegion[region].push({ code, hype: r.hype_total ?? 0 });
  }
  // also pad regions with empty seeds so the map is never empty
  for (const region of Object.keys(byRegion) as Region[]) {
    if (byRegion[region].length < 6) {
      const known = new Set(byRegion[region].map((x) => x.code));
      Object.entries(COUNTRY_REGION)
        .filter(([, r]) => r === region)
        .map(([c]) => c)
        .filter((c) => !known.has(c))
        .slice(0, 6 - byRegion[region].length)
        .forEach((c) => byRegion[region].push({ code: c, hype: 0 }));
    }
    byRegion[region].sort((a, b) => b.hype - a.hype);
  }

  return (
    <section
      aria-label="Global Heat Map"
      className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[oklch(0.10_0.04_260)] via-[oklch(0.08_0.03_280)] to-[oklch(0.06_0.02_300)] p-4 shadow-[0_20px_60px_-20px_rgba(0,0,0,.65)]"
    >
      <header className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Globe2 className="h-4 w-4 text-cyan-300" />
          <h2 className="text-xs font-black uppercase tracking-[0.18em] text-white/90">Global Heat Map™</h2>
        </div>
        <span className="rounded-full bg-rose-500/20 px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-rose-200">
          ● LIVE
        </span>
      </header>
      <div className="space-y-3">
        {REGIONS.map((region) => (
          <div key={region.id}>
            <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-white/40">{region.label}</div>
            <div className="grid grid-cols-6 gap-1 sm:grid-cols-8">
              {byRegion[region.id].map(({ code, hype }) => {
                const intensity = max > 0 ? Math.min(1, hype / max) : 0;
                return (
                  <div
                    key={code}
                    title={`${code} · ${hype.toLocaleString()} hype`}
                    className="group relative aspect-square overflow-hidden rounded-md border border-white/10 transition-transform hover:scale-110"
                    style={{
                      background: intensity > 0
                        ? `radial-gradient(circle at center, ${heatColor(intensity)} 0%, transparent 80%)`
                        : "rgba(255,255,255,0.03)",
                      boxShadow: intensity > 0.6 ? `0 0 16px ${heatColor(intensity)}` : undefined,
                    }}
                  >
                    <div className="flex h-full w-full flex-col items-center justify-center">
                      <span className="text-base leading-none">{FLAG[code] ?? "🏳️"}</span>
                      <span className="mt-0.5 text-[8px] font-black tracking-wide text-white/80">{code}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <p className="mt-3 text-[10px] text-white/40">Intensidad por país · color reactivo a pulsos en tiempo real</p>
    </section>
  );
}